import {useMemo, useRef} from 'react';
import {Moment} from 'moment';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {DateRangeType} from '../../../date-range-input/date-range-utils';
import {CareServicesBody} from '../../base/body/care-services-body';
import {
  OnCareServiceCreatingItem,
  OnCareServiceEditItem,
  OnCareServiceClickItem,
} from '../../base/data/care-service-item';
import {CareServicesData} from '../../base/data/use-care-services-data';
import {CareServiceItem} from '../../base/data/care-service-item';
import {
  DateRangeItem,
  CareServicesTable,
  useDateRange,
} from './care-services-table/care-services-table';

interface SeniorsCareServicesBodyProps {
  data: CareServicesData;
  startDate: Moment;
  endDate: Moment;
  range: DateRangeType;
  canWrite?: boolean;
  onCreatingItem: OnCareServiceCreatingItem;
  onClickItem: OnCareServiceClickItem;
  onEditItem: OnCareServiceEditItem;
}

export function SeniorsCareServicesBody({
  onCreatingItem,
  onClickItem,
  onEditItem,
  ...props
}: SeniorsCareServicesBodyProps) {
  const caregiversKV = useRef<Map<string, AffiliatedUser>>(new Map()); // key-value map for quick lookup

  const dateColumns = useDateRange(props.startDate, props.endDate);

  const rows = useMemo(() => {
    // If data is still fetching, do nothing.
    if (props.data.fetching) return [];

    // console.log('Rows recalculated');
    // Prepare caches for quick lookup
    function cacheCaregivers(ids?: string[]) {
      if (!ids) return;
      for (const caregiverId of ids) {
        if (caregiversKV.current.has(caregiverId)) continue;
        const caregiver = props.data.caregivers.items?.find((x) => x.user.id === caregiverId);
        if (caregiver) caregiversKV.current.set(caregiver.user.id!, caregiver);
      }
    }

    const grid: Map<AffiliatedUser, Map<DateRangeItem, CareServiceItem[]>> = new Map();

    for (const item of props.data.items) {
      const seniorId = item.targetUserId;
      const dateId = item.startDate.format('YYYY-MM-DD');
      const senior = props.data.seniors.items?.find((x) => x.user.id === seniorId);
      if (!senior) {
        console.debug('Senior not found for Care Service Item. Perhaps an inactive user.', item);
        continue;
      }

      if (!grid.has(senior)) {
        grid.set(senior, new Map());
      }
      const row = grid.get(senior)!;
      const date = dateColumns.find((d) => d.id === dateId);
      if (!date) continue;

      if (!row.has(date)) {
        row.set(date, []);
      }
      const cell = row.get(date)!;
      cell.push(item);

      // Cache seniors and caregivers
      cacheCaregivers(item.responsibleUserIds);
    }

    const rows = Array.from(grid.entries());
    // sort by senior name
    rows.sort((a, b) => {
      const nameA = a[0].user.displayName || a[0].user.name;
      const nameB = b[0].user.displayName || b[0].user.name;
      return nameA.localeCompare(nameB);
    });
    // sort items in each cell by start time
    rows.forEach(([_, dateMap]) => {
      dateMap.forEach((items, _date) => {
        items.sort((a, b) => a.startDate.valueOf() - b.startDate.valueOf());
      });
    });

    return rows;
  }, [
    dateColumns,
    props.data.caregivers.items,
    props.data.fetching,
    props.data.items,
    props.data.seniors.items,
  ]);

  const viewClassname = useMemo(() => {
    if (props.range === '1d') return 'day';
    if (props.range === '1m') return 'month';
    return 'week';
  }, [props.range]);

  return (
    <CareServicesBody isLoading={props.data.fetching} noPadding>
      <CareServicesTable dataUpdating={props.data.updating} className={viewClassname}>
        <CareServicesTable.Header
          canWrite={props.canWrite}
          columns={dateColumns}
          onCreatingItem={onCreatingItem}
        />
        <CareServicesTable.Rows
          canWrite={props.canWrite}
          rows={rows}
          columns={dateColumns}
          caregiversKV={caregiversKV.current}
          onCreatingItem={onCreatingItem}
          onClickItem={onClickItem}
          onEditItem={onEditItem}
        />
      </CareServicesTable>
    </CareServicesBody>
  );
}
